import clsx from 'clsx'

// Components
import Tooltip from '@/components/ui/tooltip/Tooltip'
import { useTruncationTooltip } from '@/components/ui/tooltip/useTruncationTooltip'

export type BreadcrumbItemType = {
  /**
   * A unique ID for the breadcrumb item
   */
  id: string
  /**
   * The label of the breadcrumb item
   */
  label: string
  /**
   * The onClick handler for the breadcrumb item
   */
  onClick?: () => void
}

export type BreadcrumbTextSize = 'sm' | 'base'

type BreadcrumbItemProps = {
  /**
   * The item to display in the breadcrumb
   */
  item: BreadcrumbItemType
  /**
   * Whether the item is active i.e. the current page
   */
  isActive?: boolean
  /**
   * The maximum character length of the label to display (non-active segments).
   */
  maxLabelLength: number

  /**
   * Maximum length of the active label to display.
   */
  maxActiveLabelLength: number

  /**
   * Text size variant for breadcrumb labels.
   */
  textSize?: BreadcrumbTextSize
}

const truncateLabel = (label: string, maxLabelLength: number): string => {
  if (label.length <= maxLabelLength) {
    return label
  }
  return `${label.slice(0, maxLabelLength)}...`
}

export function BreadcrumbItem({ item, isActive = false, maxLabelLength, maxActiveLabelLength, textSize = 'sm' }: BreadcrumbItemProps) {
  const maxLength = isActive ? maxActiveLabelLength : maxLabelLength
  const displayLabel = truncateLabel(item.label, maxLength)

  const { labelRef, showTooltip } = useTruncationTooltip({
    displayLabel,
    isActive,
    textSize,
    truncated: item.label.length > maxLength,
  })

  // Active items are non-interactive by default, but an onClick overrides that
  // e.g. when a navigable item is the only breadcrumb and therefore the active item (last item)
  const content =
    isActive && !item.onClick ? (
      <span
        ref={labelRef}
        className={clsx(
          'block w-full min-w-0 font-semibold text-brand-neutral-950 cursor-default text-nowrap overflow-hidden text-ellipsis whitespace-nowrap px-2',
          textSize === 'base' ? 'text-base py-0' : 'text-sm py-0.5'
        )}
      >
        {displayLabel}
      </span>
    ) : (
      <button
        type="button"
        onClick={item.onClick}
        className={clsx(
          'min-w-0 max-w-full text-left font-semibold text-brand-neutral-600 cursor-pointer rounded-md px-2 hover:bg-brand-neutral-100 hover:text-brand-neutral-600 active:bg-brand-neutral-100 active:text-brand-neutral-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
          textSize === 'base' ? 'text-base py-0' : 'text-sm py-0.5'
        )}
      >
        <span ref={labelRef} className="block min-w-0 truncate">
          {displayLabel}
        </span>
      </button>
    )

  return (
    <Tooltip title={showTooltip ? item.label : ''} placement="top-start" offsetX={2} offsetY={-10}>
      <span className="inline-flex min-w-0 w-full max-w-full items-center">{content}</span>
    </Tooltip>
  )
}
